import LogRocket from 'logrocket'

export const OSP_TRACKING_EVENTS = {
  STEP_VIEWED: 'OSP1 Step Viewed',
  FIELD_FOCUSED: 'OSP1 Field Focused',
  FIELD_LEFT: 'OSP1 Field Left',
  FIELD_ERROR: 'OSP1 Field Error',
  DECISION_CHANGED: 'OSP1 Decision Changed',
  SUBMIT_CLICKED: 'OSP1 Submit Clicked',
  STEP_LEFT: 'OSP1 Step Left',
  OSP2_STEP_VIEWED: 'OSP2 Step Viewed',
  OSP2_VERIFICATION_STARTED: 'OSP2 Verification Started',
  OSP2_CAMERA_STARTED: 'OSP2 Camera Started',
  OSP2_CAMERA_READY: 'OSP2 Camera Ready',
  OSP2_VERIFICATION_ERROR: 'OSP2 Verification Error',
  OSP2_VERIFICATION_COMPLETED: 'OSP2 Verification Completed',
  ORDER_COMPLETED: 'Order Completed',
} as const

export type OspTrackingProps = Record<string, string | number | boolean | string[] | null | undefined>

export function trackOspEvent (name: string, props: OspTrackingProps) {
  try {
    LogRocket.track(name, props)
  } catch (e) {
    console.error('Failed to track OSP event', e)
  }
}

const FIELD_ID_SUFFIX = /-input$/
const ADDRESS_PART_SUFFIX = /-(zip|city|street|number)-input$/
const ADDRESS_PART_KEYS: Record<string, string> = { zip: 'zip_code' }
const CONTROL_ROOT_ID_PREFIX = /^#\//
const IGNORED_PATH_SEGMENTS = new Set(['#', 'properties', 'items'])

export function normalizeOspFieldId (id: string): string {
  return id
    .replace('--', '/')
    .replace(ADDRESS_PART_SUFFIX, (_, part) => `/${ADDRESS_PART_KEYS[part] ?? part}`)
    .replace(FIELD_ID_SUFFIX, '')
    .split('/')
    .filter(segment => segment && !IGNORED_PATH_SEGMENTS.has(segment) && isNaN(Number(segment)))
    .join('/')
}

function isFieldElementId (id: string | null): id is string {
  return !!id && FIELD_ID_SUFFIX.test(id)
}

function isControlRootId (id: string | null): id is string {
  return !!id && (FIELD_ID_SUFFIX.test(id) || CONTROL_ROOT_ID_PREFIX.test(id))
}

export function resolveOspFieldKey (target: EventTarget | null, root: Element): string | null {
  if (!(target instanceof Element)) {
    return null
  }

  const ownId = target.getAttribute('id')
  if (isFieldElementId(ownId)) {
    return normalizeOspFieldId(ownId)
  }

  const name = target.getAttribute('name')
  if (name && name.includes('/')) {
    return normalizeOspFieldId(name)
  }

  let el: Element | null = target.parentElement
  while (el && el !== root) {
    const id = el.getAttribute('id')
    if (isControlRootId(id)) {
      const controlKey = normalizeOspFieldId(id)
      return name && !FIELD_ID_SUFFIX.test(id) ? `${controlKey}/${name}` : controlKey
    }
    el = el.parentElement
  }

  return name ? normalizeOspFieldId(name) : null
}

export function isOspFieldFilled (target: EventTarget | null, root: Element): boolean {
  if (!(target instanceof HTMLElement)) {
    return false
  }

  if (target instanceof HTMLInputElement) {
    if (target.type === 'checkbox') {
      return target.checked
    }
    if (target.type === 'radio') {
      return !!root.querySelector(`input[type="radio"][name="${target.name}"]:checked`)
    }
    if (target.classList.contains('vs__search')) {
      return !!target.closest('.v-select')?.querySelector('.vs__selected')
    }
    return target.value.trim() !== ''
  }

  if (target instanceof HTMLTextAreaElement) {
    return target.value.trim() !== ''
  }

  return false
}
